import type { ToastMessageOptions } from "primevue/toast";
import { useAuth } from "~/store/auth";
import { useSettings } from "~/store/settings";
import t from "~/utils/translate";
import { getDNSSuffix } from "../getDNSSuffix";

interface InternalFetch {
	type?:
		| "config"
		| "auth"
		| "sports"
		| "horses"
		| "betting"
		| "bettingHorses"
		| "api"
		| "apic"
		| "bannerDomain"
		| "casinoApi"
		| "casinoApiCdn"
		| "universal"
		| "content"
		| "player"
		| "casinoBonus"
		| "forms"
		| "insurance"
		| "graphQl"
		| "loyalty"
		| "avatar"
		| "sumsub"
		| "zaJackpots";
	url: string;
	apiVersion?: string | null;
	data?:
		| {
				[x: string]: unknown;
		  }
		| [x: unknown]
		// biome-ignore lint/suspicious/noExplicitAny:
		| any[]
		| null
		// biome-ignore lint/suspicious/noExplicitAny:
		| any;
	options?: RequestInit;
	showToast?: boolean;
	signal?: AbortSignal;
}

interface APIError extends Error {
	details: {
		errorCode: number;
		errorMessage: string;
		raw: unknown;
		res?: string;
		rUrl: string;
		token: string | null;
	};
}

const triggerToast = (showToast: boolean) => {
	if (showToast) {
		const nuxtApp = useNuxtApp();
		const getToast = () => nuxtApp.vueApp.config.globalProperties.$toast;
		const toast = {
			add(msg: ToastMessageOptions) {
				getToast().add(msg);
			},
		};
		toast.add({ severity: "error", summary: t("error.title"), detail: t("something-went-wrong"), life: 5000 });
	}
};

const api = <T>({ url, type, options, apiVersion = null, data, showToast = false, signal }: InternalFetch): Promise<T> => {
	const config = useRuntimeConfig();
	const store = useSettings();
	const auth = useAuth();
	const brandId = store.config?.appSetting?.BrandId;

	const defaultHeaders = {
		"Content-Type": "application/json",
	};

	const baseList: { [x: string]: { baseUrl: string; headers?: object; apiVersion?: string | null } } = {
		avatar: {
			baseUrl: config.public.avatarUrl,
			headers: {
				"Content-type": "application/octet-stream",
				"x-ms-blob-type": "BlockBlob",
			},
		},
		config: {
			baseUrl: process.server ? config.public.configDomain.replace("https://", "http://") : config.public.configDomain,
			headers: { "Content-Type": "text/plain" },
		},
		auth: {
			baseUrl: getDNSSuffix(config.public.authDomain),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "X-Brand-Id": brandId },
		},
		player: {
			baseUrl: getDNSSuffix(config.public.playerDomain),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "X-Brand-Id": brandId },
			apiVersion: "v1",
		},
		sports: {
			baseUrl: getDNSSuffix(store.isRadarEnabled ? config.public.sportsDomainRadar : config.public.sportsDomain),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "" },
			apiVersion: "v1",
		},
		horses: {
			baseUrl: getDNSSuffix(config.public.sportsDomainHorses),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "" },
			apiVersion: "v1",
		},
		betting: {
			baseUrl: getDNSSuffix(store.isRadarEnabled ? config.public.bettingDomainRadar : config.public.bettingDomain),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "X-Brand-Id": brandId },
			apiVersion: "v1",
		},
		bettingHorses: {
			baseUrl: getDNSSuffix(config.public.bettingDomainHorses),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "" },
			apiVersion: "v1",
		},
		content: {
			baseUrl: `${config.public.kenticoDomain}/gmapi/Content/cmsget/`,
			headers: { "Content-Type": "text/plain" },
		},
		api: {
			baseUrl: config.public.apiDomain,
			headers: {
				Authorization: auth.token ? `Bearer ${auth.token}` : "",
				"Content-Type": options?.method === "POST" || options?.method === "PUT" ? "application/json" : "text/plain",
			},
			apiVersion: "v1",
		},
		insurance: {
			baseUrl: config.public.insuranceDomain,
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "X-Brand-Id": brandId },
			apiVersion: "v1",
		},
		apic: {
			baseUrl: config.public.apicDomain,
			headers: { "X-BrandId": store.config?.appSetting?.BrandId, "Content-Type": "text/plain" },
			apiVersion: "v1",
		},
		bannerDomain: {
			baseUrl: "https://apic.betwayafrica.com",
			headers: { "X-BrandId": store.config?.appSetting?.BrandId },
			apiVersion: "",
		},
		casinoApi: {
			baseUrl: config.public.casinoApiDomain,
			headers: {
				Authorization: auth.token ? `Bearer ${auth.token}` : "",
				"Content-Type": options?.method === "POST" || options?.method === "PUT" ? "application/json" : "text/plain",
			},
			apiVersion: "v3",
		},
		casinoBonus: { baseUrl: config.public.casinoBonusDomain, headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "" }, apiVersion: "v1" },
		casinoApiCdn: {
			baseUrl: config.public.casinoApiCdnDomain,
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "Content-Type": "text/plain" },
			apiVersion: "v3",
		},
		universal: {
			baseUrl: getDNSSuffix(config.public.universalDomain),
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "X-Brand-Id": brandId },
			apiVersion: "v1",
		},
		forms: { baseUrl: config.public.wmsConfigFormDomain, headers: { "Content-Type": "text/plain" } },
		graphQl: {
			baseUrl: getDNSSuffix(config.public.graphqlUrl),
			headers: {
				Authorization: auth.token ? `Bearer ${auth.token}` : "",
				"Content-Type": "application/json",
				"X-LocationIso": store?.config?.appSetting?.CountryCode || "TZ",
				"Accept-Language": store.locale ?? "en-US",
			},
		},
		loyalty: {
			baseUrl: config.public.loyaltyUrl,
			headers: {
				Authorization: auth.token ? `Bearer ${auth.token}` : "",
				"Content-Type": "application/json",
				"x-api-version": "1.0",
				"x-correlation-id": "00000000-0000-0000-0000-000000000000",
				"Accept-Language": store.locale ?? "en-US",
			},
		},
		zaJackpots: {
			baseUrl: config.public.zaJackpotsDomain,
			headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "", "X-Brand-Id": brandId },
		},
		sumsub: {
			baseUrl: config.public.sumsubDomain,
		},
	};
	const baseURL =
		baseList[type || "config"].baseUrl +
		(apiVersion ? "/" + apiVersion : baseList[type || "config"].apiVersion ? "/" + baseList[type || "config"].apiVersion : "");
	const app = "synapse";
	const brand =
		(type === "config" || type === "content") && (store.isSportsPPC || store.isDataFree)
			? store.config?.regionCode || "TZ"
			: store?.config?.appSetting?.CountryCode || "TZ";
	const locale = store.locale ?? "en-US";
	const currency = store?.getCurrencyConfig?.currencyCode || "TZS";
	const channel = isMobile ? "WebMobile" : "WebDesktop";
	const kenticoHost = config.public.kenticoDomain.replace(/^https?:\/\//, "");
	const isLocalHost = config.public.env === "development";

	const structuredUrl = new URL(
		(baseURL.startsWith("http") ? baseURL : `${window.location.origin}${baseURL}`) +
			url
				.replace("{app}", app)
				.replace("{brand}", brand)
				.replace("{locale}", locale)
				.replace("{currency}", currency)
				.replace("{channel}", channel)
				.replace("{source}", store.isRadarEnabled ? "sportsradar" : "synapse")
				.replace("{kenticoHost}", kenticoHost)
	);
	if (isLocalHost || type === "auth") structuredUrl.searchParams.append("nogeoredirect", "1");
	if (type && ["universal", "player", "auth"].includes(type) && options?.method === "GET" && auth.loggedIn && auth.profile) {
		structuredUrl.searchParams.append("userId", auth.profile.id);
	}

	return new Promise<T>((resolve, reject) => {
		fetch(structuredUrl, {
			...options,
			body: data ? JSON.stringify(data) : null,
			signal,
			headers: {
				...defaultHeaders,
				...baseList[type || "config"].headers,
				...options?.headers,
			},
		})
			.then(async (res) => {
				if (
					res.status === 401 &&
					auth.loggedIn &&
					!structuredUrl.href.includes("authenticate") &&
					type &&
					["auth", "betting", "bettingHorses", "universal", "player"].includes(type)
				) {
					auth.logout();
					reject(res);
					return;
				}

				const resClone = res.clone();

				const resCloneString = JSON.stringify({
					status: resClone.status,
					ok: resClone.ok,
					url: resClone.url,
					type: resClone.type,
					redirected: resClone.redirected,
					headers: Array.from(resClone.headers.entries()),
					text: await resClone.text(),
					resClone: JSON.stringify(resClone),
				});

				const responseText = await res.text();

				// biome-ignore lint/suspicious/noExplicitAny: Unknown Response
				let parsed: any = {};
				try {
					parsed = responseText.length ? JSON.parse(responseText) : {};
				} catch (parseError) {
					triggerToast(showToast);

					const err = new Error("parse error");
					(err as APIError).details = {
						errorCode: -1,
						errorMessage: "Invalid JSON response",
						raw: parseError,
						res: resCloneString,
						rUrl: structuredUrl.href,
						token: auth.token,
					};
					reject(err);
					return;
				}

				if (res && (parsed?.errors || parsed?.errorCode || res.status >= 400 || parsed?.status === "Error")) {
					triggerToast(showToast);
					try {
						// parsed.errorString = resCloneString;
						reject(Object.values(parsed).length ? parsed : res);
					} catch (e) {
						triggerToast(showToast);

						const err = new Error("fail response error");
						(err as APIError).details = {
							errorCode: -1,
							errorMessage: "Unknown error",
							raw: res,
							res: resCloneString,
							rUrl: structuredUrl.href,
							token: auth.token,
						};
						reject(err);
					}
				}

				resolve(parsed);
			})
			.catch((e) => {
				triggerToast(showToast);

				const err = new Error("Network or unexpected error:");
				(err as APIError).details = {
					errorCode: -1,
					errorMessage: e?.message ?? "Network or unexpected error:",
					raw: e,
					rUrl: structuredUrl.href,
					token: auth.token,
				};
				reject(err);
			});
	});
};

export default api;
